import { useEffect, useState } from "react";
import {downloadUsersWorkbook, getUsers} from "../../api/user/user.api";
import SearchInput from "../SearchInput";
import {Button, CalendarBar, Pagination, RadioGroup, Table, Tag} from "omoplay";
import SuspensePopup from "../SuspensePopup";
import KakaoAuthorization from "../form/message/KakaoAuthorization";
import moment from "moment";
import GenericBoard from "../form/GenericBoard";
import {
  DOWNLOAD_FAILED_MESSAGE,
  isDownloadableBlobResponse,
  saveBlobResponse,
} from "../../libs/workbookDownload";

const UserListPage = () => {
  const [users, setUsers] = useState({kakaoUsers: []});
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState({});
  const [openDownloadAuth, setOpenDownloadAuth] = useState(false)
  const [isAuth, setAuth] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [isFriend, setFriend] = useState(null)

  useEffect(() => {
    getUsers(search, dateRange?.startDate, dateRange?.endDate, isFriend, page - 1, 10).then((res) => {
      if (res.code === 2000) {
        setUsers(res.data);
      } else {
        alert(res.message);
      }
    })
  }, [search, dateRange, page, isFriend]);

  const handleSearchChange = (search) => {
    setPage(1)
    setSearch(search);
  };

  const onDateInput = (dateRange) => {
    setPage(1)
    setDateRange({
      startDate: moment(dateRange.startDate).format("yyyy-MM-DD"),
      endDate: moment(dateRange.endDate).format("yyyy-MM-DD")
    });
  };

  const headers = [
    { name: "이름", align: "center", width: "100px" },
    { name: "성별", align: "center", width: "80px" },
    { name: "나이", align: "center", width: "100px" },
    { name: "생년월일", align: "center", width: "120px" },
    { name: "진입점", align: "center", width: "140px" },
    { name: "전화번호", width: "140px" },
    { name: "배송지 주소", width: "180px" },
    { name: "고객 전환 날짜", align: "center", width: "130px" },
    { name: "수신 동의" },
  ];

  /**
   * 고객 목록 엑셀 다운로드. (KU-5f, 2026-09-10)
   *
   * ### 중복 클릭을 막는 이유
   *
   * 이 요청 하나가 **채널 전체 회원을 서버 메모리로 올린다** — 상한이 없다
   * (`KakaoUserService.getPayloads`). 실측 최대 채널이 37,587행이고, 파드는
   * `resources.limits` 없이 4Gi 요청만 걸려 있다. 느리다고 느낀 사용자가 여러 번 누르면
   * **누른 횟수만큼 채널 전체 export 가 동시에 돈다.** 이게 파드를 눕히는 가장 현실적인 경로다.
   * (분석 = `_ai-runs/2026-09-10_001_kakao_user_list_and_click_user_merge/003_ku5_excel_download_risk.md`)
   *
   * ⚠️ `LoadingProvider` 의 전역 오버레이(`z-index:1000`)가 모달(`z-index:100`) 위를 덮으므로
   * 실제로는 클릭이 이미 막혀 있을 수 있다. 그래도 **거기에 기대지 않는다** —
   * 누군가 `getBlob` 에 `skipLoading` 을 넘기는 순간 그 보호가 조용히 사라진다.
   *
   * ### 실패를 성공으로 처리하지 않는 이유
   *
   * `fetch.js` 의 `getBlob` 은 실패를 **reject 하지 않는다.** `checkAuthorization` 이 axios
   * 에러 객체를 그대로 돌려주므로 `res.data` 가 `undefined` 가 된다. 종전 코드는 그것을
   * `new Blob([res.data])` 에 그대로 넣어 문자열 `"undefined"` 가 담긴 **9바이트 파일**을
   * `downloaded_file.xlsx` 라는 이름으로 사용자에게 줬다. 실패했는데 성공으로 보인다.
   * 판정은 `isDownloadableBlobResponse` 가 한다(테스트로 고정).
   *
   * ⚠️ `getBlob` 은 일부 경로에서 **영원히 pending 인 Promise** 를 돌려준다
   * (타 탭 채널 전환 / 페이지 이탈 / 400·403). 그러면 `finally` 가 실행되지 않아 버튼이
   * '다운로드 중…' 으로 남는다. 그 상황은 모두 **화면 자체가 이미 차단·이탈 상태**이므로
   * (새로고침 안내가 떠 있거나 페이지가 넘어간다) 별도 타임아웃을 두지 않았다.
   */
  const downloadUsers = () => {
    if (downloading) return

    const { startDate, endDate } = dateRange;

    setDownloading(true)
    downloadUsersWorkbook(search, isFriend, startDate, endDate)
        .then((res) => {
          if (!isDownloadableBlobResponse(res)) {
            alert(DOWNLOAD_FAILED_MESSAGE)
            return
          }
          saveBlobResponse(res, 'omoplay_users.xlsx')
        })
        .catch(() => alert(DOWNLOAD_FAILED_MESSAGE))
        .finally(() => setDownloading(false))
  }

  function userDetail(id) {
    window.location.href = `/user/detail?id=${id}`
  }

  const tableData = users.kakaoUsers.map(user => {
    return [
        <div className="flex-center-all h-full">
            <Tag>{user.spotName ?? "온라인 (자사몰)"}</Tag>
        </div>,
        <div className="flex-center-all h-full gap-2 text-primary pointer" onClick={() => userDetail(user.id)}>
          {user.name}
        </div>,
        <div className="flex-align-center h-full">
          <Tag className="text-white" color={user.gender === "남성" ? "#708090" : "#FFC0CB"}>{user.gender}</Tag>
        </div>,
        <div className="flex-col flex-center-all h-full gap-2">
          <div>{user.ageRange}</div>
        </div>,
        <div className="flex-col flex-center-all h-full gap-2">
          <div>{user.birthday}</div>
        </div>,
        <div className="flex-align-center h-full">{user.phoneNumber}</div>,
        <div className="flex-align-center h-full">
        <div className="float-left max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap">
          {user.baseAddress}
        </div>
        </div>,
        <div className="flex-align-center text-center h-full">
          {user.createdAt}
        </div>,
        <div className="flex-align-center h-full gap-2">
          <Tag color={user.friendStatus === "친구" ? "success" : "error"}>{user.friendStatus}</Tag>
          <Tag color={user.smsAgreed === "동의" ? 'info' : 'warning'}>SMS {user.smsAgreed}</Tag>
          <Tag color={user.emailAgreed === "동의" ? 'info' : 'warning'}>Email {user.emailAgreed}</Tag>
        </div>,
    ]
  })

  return (
    <>
      <div className="flex-col rounded">
        <div className="flex items-start mt-[35px] gap-[24px]">
          <div className="max-w-[405px] w-[405px]">
            <div className="mb-[9px]">
              <label className="search-label">검색어</label>
            </div>
            <SearchInput onSubmit={(text) => handleSearchChange(text)} />
          </div>
          <div>
            <div className="mb-[9px]">
              <label className="search-label">고객 전환일</label>
            </div>
            <CalendarBar onConfirm={(props) => onDateInput(props)} />
          </div>
          <div>
            <div className="mb-[20px]">
              <label className="search-label">친구 상태</label>
            </div>
            <RadioGroup items={[{label: "전체", value: null}, {label: "친구", value: "true"}, {label: "차단", value: "false"}]}
                        onSelected={(value) => {
                          setFriend(value === null ? null : value === "true")
                        }}
            />
          </div>
            <div>
                <div className="mb-[29px]"></div>
                <Button variants="primary" value="고객 다운로드" onClick={() => setOpenDownloadAuth(true)} />
            </div>
        </div>
        <div className="flex justify-between items-end mt-[30px] ml-[14px]">
          <span className="font-16px">
            총 <span className="text-primary">{users.totalCount?.toLocaleString()}</span>명
          </span>
        </div>
        <div className="flex-col py-4">
            <GenericBoard items={tableData} columns={[
                { align: 'left', flex: true },
                { align: 'left', width: 'w-16' },
                { align: 'left', width: 'w-16' },
                { align: 'left', width: 'w-8' },
                { align: 'left', width: 'w-16' },
                { align: 'left', width: 'w-32' },
                { align: 'left', width: 'w-32' },
                { align: 'right', width: 'w-48' },
                { align: 'right', width: 'w-52' }
            ]} />
          <div className="flex justify-center mt-[24px]">
            <Pagination pageSize={10} totalCount={users.totalCount} currentPage={page} onPaging={(page) => setPage(page)} />
          </div>
        </div>
      </div>
      <SuspensePopup
          title={"카카오 인증하기"}
          visible={openDownloadAuth}
          style={{maxWidth: "600px"}}
          onClose={() => {
            // 다운로드 중 닫으면 요청은 계속 도는데 화면에는 흔적이 없어져
            // 사용자가 다시 눌러 export 를 한 번 더 돌리게 된다.
            if (downloading) return
            setOpenDownloadAuth(false)
            setAuth(false)
          }}
      >
        <div className="flex-col gap-8">
          <KakaoAuthorization onSubmit={() => setAuth(true)} />
          <div className="flex justify-between">
            <div>{isAuth && "비밀번호: omoplay"}</div>
            <Button variants="dark" value={downloading ? "다운로드 중…" : "엑셀 다운로드"}
                    disabled={!isAuth || downloading} onClick={() => downloadUsers()} />
          </div>
        </div>
      </SuspensePopup>
    </>
  );
};
export default UserListPage;
